/**
 * API Client Factory
 * Creates a flexible and extensible HTTP client with interceptors and retry logic
 */

import type {
  ApiResponse,
  ApiRequestConfig,
  ApiClientOptions,
  RequestInterceptor,
  ResponseInterceptor,
  ErrorInterceptor,
} from './types';
import { ApiError as ApiErrorClass, NetworkError as NetworkErrorClass, HttpMethod } from './types';

/**
 * API Client Factory
 * Creates an API client with configurable interceptors and retry logic
 */
export class ApiClient {
  private baseUrl: string;
  private timeout: number;
  private defaultHeaders: Record<string, string>;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];
  private enableLogging: boolean;
  private enableRetry: boolean;
  private defaultRetries: number;
  private defaultRetryDelay: number;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl;
    this.timeout = options.timeout || 30000;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    this.enableLogging = options.enableLogging !== false;
    this.enableRetry = options.enableRetry !== false;
    this.defaultRetries = options.defaultRetries || 3;
    this.defaultRetryDelay = options.defaultRetryDelay || 1000;

    // Register interceptors
    if (options.requestInterceptors) {
      this.requestInterceptors.push(...options.requestInterceptors);
    }
    if (options.responseInterceptors) {
      this.responseInterceptors.push(...options.responseInterceptors);
    }
    if (options.errorInterceptors) {
      this.errorInterceptors.push(...options.errorInterceptors);
    }
  }

  /**
   * Add a request interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add a response interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * Add an error interceptor
   */
  addErrorInterceptor(interceptor: ErrorInterceptor): void {
    this.errorInterceptors.push(interceptor);
  }

  /**
   * Execute request interceptors
   */
  private async executeRequestInterceptors(config: ApiRequestConfig): Promise<ApiRequestConfig> {
    let result = config;

    for (const interceptor of this.requestInterceptors) {
      result = await Promise.resolve(interceptor(result));
    }

    return result;
  }

  /**
   * Execute response interceptors
   */
  private async executeResponseInterceptors<T>(response: ApiResponse<T>): Promise<ApiResponse<T>> {
    let result: ApiResponse<T> = response;

    for (const interceptor of this.responseInterceptors) {
      result = await Promise.resolve(
        interceptor(result as ApiResponse<unknown>) as Promise<ApiResponse<T>>
      );
    }

    return result;
  }

  /**
   * Execute error interceptors
   */
  private async executeErrorInterceptors(error: Error): Promise<Error> {
    let result = error;

    for (const interceptor of this.errorInterceptors) {
      result = await Promise.resolve(interceptor(result));
    }

    return result;
  }

  /**
   * Build full URL from endpoint
   */
  private buildUrl(endpoint: string): string {
    const cleanBase = this.baseUrl.replace(/\/$/, '');
    const cleanEndpoint = endpoint.replace(/^\//, '');
    return `${cleanBase}/${cleanEndpoint}`;
  }

  /**
   * Build request headers
   */
  private buildHeaders(config: ApiRequestConfig): Record<string, string> {
    return {
      ...this.defaultHeaders,
      ...config.headers,
    };
  }

  /**
   * Log request (development only)
   */
  private logRequest(method: HttpMethod, url: string, config: ApiRequestConfig): void {
    if (!this.enableLogging || config.skipLog) return;

    if (!import.meta.env.DEV) return;

    const timestamp = new Date().toISOString();
    console.warn(`[API Request] ${timestamp} ${method} ${url}`);

    if (config.params) {
      console.warn('Params:', config.params);
    }
    if (config.data) {
      console.warn('Data:', config.data);
    }
  }

  /**
   * Log response (development only)
   */
  private logResponse<T>(
    method: HttpMethod,
    url: string,
    response: ApiResponse<T>,
    duration: number
  ): void {
    if (!this.enableLogging) return;

    if (!import.meta.env.DEV) return;

    const timestamp = new Date().toISOString();
    console.warn(
      `[API Response] ${timestamp} ${method} ${url} ${response.status} (${duration.toFixed(2)}ms)`
    );
  }

  /**
   * Log error (development only)
   */
  private logError(error: Error): void {
    if (!this.enableLogging) return;

    if (!import.meta.env.DEV) return;

    const timestamp = new Date().toISOString();
    console.warn(`[API Error] ${timestamp} ${error.message}`);
  }

  /**
   * Retry with exponential backoff
   */
  private async retryRequest<T>(
    method: HttpMethod,
    url: string,
    config: ApiRequestConfig,
    attemptNumber: number
  ): Promise<T> {
    const retries = config.retries ?? this.defaultRetries;
    const baseDelay = config.retryDelay ?? this.defaultRetryDelay;

    if (attemptNumber >= retries) {
      throw new NetworkErrorClass('Max retries exceeded');
    }

    // Exponential backoff: delay = baseDelay * (2 ^ attemptNumber)
    const delay = baseDelay * Math.pow(2, attemptNumber);

    console.warn(`[API Retry] Attempting retry ${attemptNumber + 1}/${retries} after ${delay}ms`);

    await new Promise((resolve) => setTimeout(resolve, delay));

    return this.request<T>(method, url, config, attemptNumber + 1);
  }

  /**
   * Internal request method with retry logic
   */
  private async request<T>(
    method: HttpMethod,
    endpoint: string,
    config: ApiRequestConfig = {},
    attemptNumber = 0
  ): Promise<T> {
    try {
      // Execute request interceptors
      const finalConfig = await this.executeRequestInterceptors(config);

      const url = this.buildUrl(endpoint);
      const headers = this.buildHeaders(finalConfig);

      // Log request
      this.logRequest(method, url, finalConfig);

      const startTime = performance.now();

      // Build request options
      const options: RequestInit = {
        method,
        headers,
        signal: AbortSignal.timeout(finalConfig.timeout ?? this.timeout),
      };

      if (finalConfig.data) {
        options.body = JSON.stringify(finalConfig.data);
      }

      // Make request
      const response = await fetch(url, options);
      const endTime = performance.now();
      const duration = endTime - startTime;

      // Parse response
      let responseData: unknown;

      try {
        const contentType = response.headers.get('content-type') || '';

        // Detect Cloudflare challenge/block pages (HTML returned instead of JSON)
        if (
          contentType.includes('text/html') &&
          (response.status === 403 || response.status === 503)
        ) {
          throw new ApiErrorClass(
            response.status,
            'Request blocked by firewall. Please check your network connection and try again.',
          );
        }

        if (contentType.includes('application/json')) {
          responseData = await response.json();
        } else {
          responseData = await response.text();
        }
      } catch (parseError) {
        if (parseError instanceof ApiErrorClass) {
          throw parseError;
        }
        // Failed to parse response body
        responseData = null;
      }

      // Handle response
      if (!response.ok) {
        const errorDetails = this.parseErrorResponse(responseData);
        const apiError = new ApiErrorClass(response.status, errorDetails.message, {
          ...errorDetails,
          statusCode: errorDetails.statusCode ?? response.status,
        });

        // Log error
        this.logError(apiError);

        // Execute error interceptors
        const processedError = await this.executeErrorInterceptors(apiError);

        // Handle retryable errors
        if (this.enableRetry && this.isRetryableError(response.status)) {
          return this.retryRequest(method, endpoint, config, attemptNumber);
        }

        // Special handling for 401: Check if it's been marked for retry after token refresh
        if (
          response.status === 401 &&
          processedError instanceof ApiErrorClass &&
          (processedError as unknown as Record<string, boolean>).retryAfterRefresh === true
        ) {
          return this.retryRequest(method, endpoint, config, attemptNumber);
        }

        throw apiError;
      }

      // Create response object
      const apiResponse: ApiResponse<T> = {
        data: responseData as T,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        config: finalConfig,
      };

      // Log response
      this.logResponse(method, url, apiResponse, duration);

      // Execute response interceptors
      const finalResponse = await this.executeResponseInterceptors(apiResponse);

      return finalResponse.data;
    } catch (error) {
      // Handle network errors
      if (error instanceof Error && error.name === 'AbortError') {
        const networkError = new NetworkErrorClass('Request timeout', error);

        // Execute error interceptors
        await this.executeErrorInterceptors(networkError);

        // Retry on timeout
        if (this.enableRetry && attemptNumber < (config.retries ?? this.defaultRetries)) {
          return this.retryRequest(method, endpoint, config, attemptNumber);
        }

        throw networkError;
      }

      // Re-throw API errors
      if (error instanceof ApiErrorClass) {
        throw error;
      }

      // Handle other errors
      const unknownError = error instanceof Error ? error : new Error(String(error));
      await this.executeErrorInterceptors(unknownError);
      throw unknownError;
    }
  }

  /**
   * Check if error is retryable
   */
  private isRetryableError(statusCode: number): boolean {
    // Retry on 5xx and specific 4xx errors
    return (
      statusCode >= 500 || // Server errors
      statusCode === 408 || // Request timeout
      statusCode === 429 // Rate limited
    );
  }

  /**
   * Parse error response from API
   */
  private parseErrorResponse(data: unknown): {
    message: string;
    statusCode?: number;
    errors?: Record<string, string[]>;
  } {
    if (typeof data === 'object' && data !== null) {
      const errorObj = data as Record<string, unknown>;
      let message = 'Unknown error';

      if (typeof errorObj.message === 'string') {
        message = errorObj.message;
      } else if (typeof errorObj.error === 'string') {
        message = errorObj.error;
      }

      return {
        message,
        statusCode: typeof errorObj.statusCode === 'number' ? errorObj.statusCode : undefined,
        errors:
          typeof errorObj.errors === 'object'
            ? (errorObj.errors as Record<string, string[]>)
            : undefined,
      };
    }

    return {
      message: typeof data === 'string' ? data : 'Unknown error',
    };
  }

  /**
   * GET request
   */
  async get<T = unknown>(endpoint: string, config?: ApiRequestConfig): Promise<T> {
    return this.request<T>(HttpMethod.GET, endpoint, {
      ...config,
      method: HttpMethod.GET,
    });
  }

  /**
   * POST request
   */
  async post<T = unknown>(endpoint: string, data?: unknown, config?: ApiRequestConfig): Promise<T> {
    return this.request<T>(HttpMethod.POST, endpoint, {
      ...config,
      data,
      method: HttpMethod.POST,
    });
  }

  /**
   * PUT request
   */
  async put<T = unknown>(endpoint: string, data?: unknown, config?: ApiRequestConfig): Promise<T> {
    return this.request<T>(HttpMethod.PUT, endpoint, {
      ...config,
      data,
      method: HttpMethod.PUT,
    });
  }

  /**
   * PATCH request
   */
  async patch<T = unknown>(
    endpoint: string,
    data?: unknown,
    config?: ApiRequestConfig
  ): Promise<T> {
    return this.request<T>(HttpMethod.PATCH, endpoint, {
      ...config,
      data,
      method: HttpMethod.PATCH,
    });
  }

  /**
   * DELETE request
   */
  async delete<T = unknown>(endpoint: string, config?: ApiRequestConfig): Promise<T> {
    return this.request<T>(HttpMethod.DELETE, endpoint, {
      ...config,
      method: HttpMethod.DELETE,
    });
  }
}

/**
 * Create API client instance
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  return new ApiClient(options);
}
